import React, {useMemo} from 'react'
import {Button, Col, Container, Divider, Loading, Row} from 'grilnica-reactstrap'
import {
  MenuCategoryDTO,
  ProductDTO,
  ProductMap,
  PushClientDTO,
  RestaurantByProductMap,
} from 'grilnica-store-share'
import {NotificationPageImage} from './components/NotificationPageImage'
import {NotificationPageOtherProductList} from './components/NotificationPageOtherProductList'
import {NotificationPageInfo} from './components/NotificationPageInfo'

interface NotificationPageComponentProps {
  message: PushClientDTO

  categoryList: MenuCategoryDTO[]
  productMap: ProductMap
  restaurantByProductMap: RestaurantByProductMap
  hidePushMessage: () => void
  addCouponOrCouponAndProducts: () => void
  goBack: () => void
  checkIsAddProduct: (productProp: ProductDTO) => ProductDTO
}

const NotificationPageComponent: React.FC<NotificationPageComponentProps> = ({
  message,
  categoryList,
  productMap,
  restaurantByProductMap,
  hidePushMessage,
  addCouponOrCouponAndProducts,
  goBack,
  checkIsAddProduct,
}): React.ReactElement => {
  const renderActionButton: React.ReactElement = useMemo(() => {
    if (message?.buttonText) {
      return (
        <>
          <Divider className={'mb-x6'} />
          <Button
            size={'md'}
            color={'primary'}
            isRoundedButton
            onClick={() => {
              addCouponOrCouponAndProducts()
            }}>
            {message?.buttonText}
          </Button>
        </>
      )
    } else {
      return <></>
    }
  }, [addCouponOrCouponAndProducts, message?.buttonText])

  const renderMessage: React.ReactElement = useMemo(() => {
    if (message) {
      if (message.imageFileName) {
        const colSize = {
          xl: 6,
          lg: 6,
          md: 6,
          sm: 6,
          xs: 12,
        }
        return (
          <Row>
            <Col xl={6} lg={6} md={5} sm={12} xs={12}>
              <NotificationPageImage imageFileName={message.imageFileName} name={message.title} />
            </Col>
            <Col xl={6} lg={6} md={7} sm={12} xs={12}>
              <NotificationPageInfo
                message={message}
                categoryList={categoryList}
                productMap={productMap}
                restaurantByProductMap={restaurantByProductMap}
                checkIsAddProduct={checkIsAddProduct}
                hidePushMessage={hidePushMessage}
                goBack={goBack}
                colSize={colSize}
              />
              {message?.productsOther && (
                <NotificationPageOtherProductList
                  message={message}
                  productsOther={message?.productsOther}
                  categoryList={categoryList}
                  productMap={productMap}
                  restaurantByProductMap={restaurantByProductMap}
                  checkIsAddProduct={checkIsAddProduct}
                  colSize={colSize}
                />
              )}
              {renderActionButton}
            </Col>
          </Row>
        )
      } else {
        const colSize = {
          xl: 4,
          lg: 4,
          md: 6,
          sm: 6,
          xs: 12,
        }
        return (
          <Row>
            <Col
              xl={{size: 10, offset: 1}}
              lg={{size: 10, offset: 1}}
              md={{size: 8, offset: 2}}
              sm={12}
              xs={12}>
              <NotificationPageInfo
                message={message}
                categoryList={categoryList}
                productMap={productMap}
                restaurantByProductMap={restaurantByProductMap}
                checkIsAddProduct={checkIsAddProduct}
                hidePushMessage={hidePushMessage}
                goBack={goBack}
                colSize={colSize}
              />
              {message?.productsOther && (
                <NotificationPageOtherProductList
                  message={message}
                  productsOther={message?.productsOther}
                  categoryList={categoryList}
                  productMap={productMap}
                  restaurantByProductMap={restaurantByProductMap}
                  checkIsAddProduct={checkIsAddProduct}
                  colSize={colSize}
                />
              )}
              {renderActionButton}
            </Col>
          </Row>
        )
      }
    } else {
      return <></>
    }
  }, [
    categoryList,
    checkIsAddProduct,
    goBack,
    hidePushMessage,
    message,
    productMap,
    renderActionButton,
    restaurantByProductMap,
  ])

  const renderContent: React.ReactElement = useMemo(() => {
    return (
      <section className={'notification-page'}>{!!message ? renderMessage : <Loading />}</section>
    )
  }, [message, renderMessage])

  return (
    <Container size={'normal'} className={'mt-x10 mb-x10'}>
      {renderContent}
    </Container>
  )
}

export {NotificationPageComponent}
